import { Component, DestroyRef, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavbarComponent,
} from '../layout/navbar/navbar.component';
import { FooterComponent } from '../layout/footer/footer.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { ClickOutsideDirective } from '../../shared/directives/click-outside.directive';
import { DatePickerComponent } from '../../shared/components/date-picker/date-picker.component';
import {
  TranslationService
} from '../../core/services/translation.service';
import { UiPreferencesService } from '../../core/services/ui-preferences.service';
import {
  ValuationRequestService
} from '../../core/services/valuation-request.service';
import { type AppLanguageCode, type AppLanguageOption } from '../../core/models/app-language.model';
import {
  type CadastralAreaSuggestion,
  type MunicipalitySuggestion,
  type ValuationRequestResponse
} from '../../core/models/valuation-request.model';
import { CadastralAreaService } from '../../core/services/cadastral-area.service';
import { MunicipalityService } from '../../core/services/municipality.service';
import { extractHttpErrorMessage } from '../../core/utils/http-error.util';

@Component({
  selector: 'app-localization',
  standalone: true,
  imports: [ReactiveFormsModule, NavbarComponent, FooterComponent, TranslatePipe, ClickOutsideDirective, DatePickerComponent],
  templateUrl: './localization.component.html',
  styleUrl: './localization.component.css'
})
export class LocalizationComponent {
  private readonly destroyRef = inject(DestroyRef);

  protected readonly languages: readonly AppLanguageOption[];
  protected readonly darkMode;
  protected readonly form;
  protected regionMenuOpen = false;
  protected isSubmitting = false;
  protected isSearchingMunicipalities = false;
  protected isSearchingCadastralAreas = false;
  protected copyStatus: 'idle' | 'success' | 'error' = 'idle';
  protected errorMessage = '';
  protected response: ValuationRequestResponse | null = null;
  protected municipalitySuggestions: MunicipalitySuggestion[] = [];
  protected cadastralSuggestions: CadastralAreaSuggestion[] = [];
  protected readonly regionOptions = [
    'Hlavni mesto Praha',
    'Jihocesky kraj',
    'Jihomoravsky kraj',
    'Karlovarsky kraj',
    'Kralovehradecky kraj',
    'Liberecky kraj',
    'Moravskoslezsky kraj',
    'Olomoucky kraj',
    'Pardubicky kraj',
    'Plzensky kraj',
    'Stredocesky kraj',
    'Ustecky kraj',
    'Kraj Vysocina',
    'Zlinsky kraj'
  ];
  constructor(
    protected readonly translationService: TranslationService,
    private readonly uiPreferences: UiPreferencesService,
    private readonly formBuilder: FormBuilder,
    private readonly valuationRequestService: ValuationRequestService,
    private readonly cadastralAreaService: CadastralAreaService,
    private readonly municipalityService: MunicipalityService
  ) {
    this.languages = this.translationService.languageOptions;
    this.darkMode = this.uiPreferences.darkMode;
    this.form = this.formBuilder.nonNullable.group({
      region: ['', Validators.required],
      municipality: ['', Validators.required],
      valuationDate: ['', Validators.required],
      cadastralArea: ['', Validators.required]
    });

    this.form.controls.municipality.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((value) => {
        const query = value.trim();

        if (query.length < 2) {
          this.isSearchingMunicipalities = false;
          return of([]);
        }

        this.isSearchingMunicipalities = true;
        return this.municipalityService.search(query);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (suggestions) => {
        this.municipalitySuggestions = suggestions;
        this.isSearchingMunicipalities = false;
      },
      error: () => {
        this.municipalitySuggestions = [];
        this.isSearchingMunicipalities = false;
      }
    });

    this.form.controls.cadastralArea.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((value) => {
        const query = value.trim();

        if (query.length < 2) {
          this.isSearchingCadastralAreas = false;
          return of([]);
        }

        this.isSearchingCadastralAreas = true;
        return this.cadastralAreaService.search(query);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (suggestions) => {
        this.cadastralSuggestions = suggestions;
        this.isSearchingCadastralAreas = false;
      },
      error: () => {
        this.cadastralSuggestions = [];
        this.isSearchingCadastralAreas = false;
      }
    });
  }

  protected toggleTheme(): void {
    this.uiPreferences.toggleDarkMode();
  }

  protected async setLanguage(language: AppLanguageCode): Promise<void> {
    await this.translationService.setLanguage(language);
  }

  protected submit(): void {
    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }

    const { region, municipality, valuationDate, cadastralArea } = this.form.getRawValue();

    this.isSubmitting = true;
    this.errorMessage = '';
    this.response = null;

    this.valuationRequestService.create({
      region: region.trim(),
      municipality: municipality.trim(),
      cadastralArea: cadastralArea.trim(),
      valuationDate: this.toApiDate(valuationDate)
    }).subscribe({
      next: (response) => {
        this.response = response;
        this.isSubmitting = false;
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.extractErrorMessage(error);
        this.isSubmitting = false;
      }
    });
  }

  protected toggleRegionMenu(): void {
    this.regionMenuOpen = !this.regionMenuOpen;
  }

  protected closeRegionMenu(): void {
    this.regionMenuOpen = false;
  }

  protected applyRegionOption(option: string): void {
    this.form.controls.region.setValue(option);
    this.regionMenuOpen = false;
  }

  protected applyCadastralOption(option: string): void {
    this.form.controls.cadastralArea.setValue(option);
    this.cadastralSuggestions = [];
  }

  protected applyMunicipalityOption(option: string): void {
    this.form.controls.municipality.setValue(option);
    this.municipalitySuggestions = [];
  }

  protected hasError(controlName: 'region' | 'municipality' | 'valuationDate' | 'cadastralArea'): boolean {
    const control = this.form.controls[controlName];
    return control.invalid && (control.dirty || control.touched);
  }

  protected formatResponse(): string {
    return this.response?.historyText?.trim()
      || this.translationService.translate('localization.output.placeholder');
  }

  protected previewText(): string {
    if (this.response) {
      return this.formatResponse();
    }

    if (this.isSubmitting) {
      return this.translationService.translate('localization.output.loading');
    }

    return this.translationService.translate('localization.output.placeholder');
  }

  protected async copyPreviewText(): Promise<void> {
    const text = this.previewText();

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      this.copyStatus = 'success';
    } catch {
      this.copyStatus = 'error';
    }

    window.setTimeout(() => {
      this.copyStatus = 'idle';
    }, 2000);
  }

  private toApiDate(dateValue: string): string {
    return `${dateValue}T00:00:00.000Z`;
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    return extractHttpErrorMessage(error, 'Unable to create valuation request.');
  }
}
